import React from 'react'
import PropTypes from 'prop-types'
import cssModules from 'react-css-modules'
import styles from './send_amount.less'

import Avatar from 'components/common/avatar'
import Separator from 'components/common/separator'
import Checkbox from 'components/common/checkbox'
import Money from 'components/common/money'
import InputError from 'components/common/input_error'
import { POOLS_CAMPAIGN_TYPES, AMOUNT_TYPES } from 'shared/shared_consts'
import SubmitLink from 'components/common/submit_link'
import { glimpseRoute } from 'lib/route_utils'
import { getI18n } from 'client/i18n'
import { formatMoney } from 'client/griffin'

const { CROWD_FUNDING } = POOLS_CAMPAIGN_TYPES

const AUTOFOCUS_TIMEOUT = 250
const i18n = getI18n('components/send_glimpse/send_amount')

export function amountTypeToLabel(amountType) {
  let amountI18nlabel
  switch (amountType) {
    case AMOUNT_TYPES.exactly:
      amountI18nlabel = 'exact_amount'
      break
    case AMOUNT_TYPES.at_least:
      amountI18nlabel = 'min_amount'
      break
    default:
      amountI18nlabel = null
      break
  }
  return amountI18nlabel
}

class SendAmount extends React.Component {
  static propTypes = {
    displayName: PropTypes.string,
    currency: PropTypes.string,
    amountType: PropTypes.oneOf(
      Object.keys(AMOUNT_TYPES).map(key => AMOUNT_TYPES[key])
    ),
    title: PropTypes.string,
    campaignType: PropTypes.string,
    fields: PropTypes.object,
    handleSubmit: PropTypes.func,
    location: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
    anyInactiveFieldsTouchedWithError: PropTypes.bool.isRequired,
    campaignId: PropTypes.string.isRequired,
    shouldAutoFocus: PropTypes.bool,
    isGuest: PropTypes.bool,
    countryCode: PropTypes.string,
    locale: PropTypes.string,
    contributorCommentDisabled: PropTypes.bool,
  }

  render() {
    const {
      fields: { amount, anonymous },
      handleSubmit,
      anyInactiveFieldsTouchedWithError,
      isGuest,
      countryCode,
      locale,
      campaignType,
      contributorCommentDisabled,
    } = this.props
    const noteRoute = isGuest
      ? glimpseRoute('guest', 'note')
      : glimpseRoute('send', 'note')
    const nextRoute = contributorCommentDisabled
      ? glimpseRoute('send', 'review')
      : noteRoute
    const tcLink = `/${countryCode}/webapps/mpp/ua/generositynetwork-tnc?locale.x=${locale}`

    const formattedAmount = formatMoney(
      this.props.currency,
      amount.initialValue,
      { removeTrailingZeros: false }
    )
    const infoLabel = i18n(amountTypeToLabel(this.props.amountType), {
      amount: formattedAmount,
    })
    const titleKey = campaignType === CROWD_FUNDING ? 'cf_title' : 'title'
    const anonymousKey =
      campaignType === CROWD_FUNDING
        ? 'cf_show_name_and_amount'
        : 'show_name_and_amount'
    let gdpr
    if (campaignType === CROWD_FUNDING) {
      let gdprContent = i18n('cf_gdpr_disclaimer', { tcLink })
      gdpr = (
        <p
          styleName="gdpr-text"
          dangerouslySetInnerHTML={{
            __html: gdprContent,
          }}
        />
      )
    } else {
      let gdprContent = isGuest
        ? i18n('guest_gdpr_disclaimer')
        : i18n('gdpr_disclaimer')
      gdpr = <p styleName="gdpr-text">{gdprContent}</p>
    }
    return (
      <div>
        <div styleName="avatar-container">
          <Avatar campaignId={this.props.campaignId} />
        </div>
        <div styleName="title-container">
          <div styleName="title">{i18n(titleKey)}</div>
          <div styleName="recipient-name">{this.props.displayName}</div>
        </div>
        <div styleName="clearer" />
        <Separator type="dotted" />
        <form>
          <div styleName="money-container">
            <Money
              size="big"
              editable={this.props.amountType !== AMOUNT_TYPES.exactly}
              focusOnMount={
                this.props.shouldAutoFocus ? AUTOFOCUS_TIMEOUT : null
              }
              removeTrailingZeros={false}
              currency={this.props.currency}
              value={amount.value || 0}
              onChange={amount.onChange}
            />
          </div>
          <div styleName="input-error-container">
            <InputError
              errors={amount.error}
              align="center"
              showAsError={amount.touched && !!amount.error}
              nonErrors={
                this.props.amountType !== AMOUNT_TYPES.any ? infoLabel : ''
              }
            />
          </div>
          <Separator type="dotted" />
          <div styleName="for">{i18n('for')}</div>
          <div styleName="event-title">{this.props.title}</div>
          <Separator type="dotted" />
          <div styleName="show-name-checkbox-container">
            {/*
								NOTE: It is counter intuitive that the click name is anonymousOff when
								anonymous.value === true, but it is correct. This app was setup up to use
								an event listener outside of react for handling click tracking. The click
								track is fired before that value is updated. We must make sure the attr
								has the correct value when pa.js grabs it, and not after react rerenders (too late).
								This might drive you crazy, but prefer it over convoluted setTimeouts, etc.
						*/}
            <Checkbox
              label={i18n(anonymousKey)}
              name="show_name_and_amount"
              data-pa-click={`anonymous${anonymous.value ? 'Off' : 'On'}`}
              value={!!anonymous.value}
              onChange={anonymous.onChange}
              smallText={true}
            />
          </div>
          <SubmitLink
            handleSubmit={handleSubmit}
            data-pa-click="next"
            to={nextRoute}
            className={`vx_btn vx_btn-block ${
              anyInactiveFieldsTouchedWithError ? 'vx_btn-disabled' : ''
            }`}
            disabled={anyInactiveFieldsTouchedWithError}
            text={i18n('next')}
            dataNemo="next"
          />
        </form>
        {gdpr}
      </div>
    )
  }
}

export default cssModules(SendAmount, styles)
